/**
 * InlineNameEditor Component
 *
 * Inline text input for editing geometry names.
 * Supports Enter to save, Escape to cancel, validation, and auto-focus.
 * Features confirm/cancel buttons with mobile-aware tooltips.
 *
 * @module components/map/GeometriesList/core
 */

import {
  memo,
  useState,
  useEffect,
  useRef,
  useCallback,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput, ActionIcon, Group, Tooltip } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import {
  GEOMETRY_NAME_MAX_LENGTH,
  GEOMETRY_NAME_MIN_LENGTH,
} from '../../../../constants/geometry';
import { ICON_SIZE_MD } from '../../../../constants/ui';
import { Z_INDEX_TOOLTIP } from '../../../../constants/zIndex';
import { useDeviceLayout } from '../../../../hooks/useDeviceLayout';
import type { InlineNameEditorProps } from '../types';
import classes from '../styles/InlineNameEditor.module.css';

/**
 * InlineNameEditor Component
 *
 * Provides inline editing for geometry names with keyboard shortcuts.
 */
export const InlineNameEditor = memo(function InlineNameEditor({
  value,
  onSave,
  onCancel,
  maxLength = GEOMETRY_NAME_MAX_LENGTH,
  placeholder,
  isActive,
  className,
}: InlineNameEditorProps) {
  const { t } = useTranslation();
  const { isMobile } = useDeviceLayout();
  const [editValue, setEditValue] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus and select text when editor becomes active
  useEffect(() => {
    if (isActive && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isActive]);

  // Validation
  const validate = useCallback(
    (newValue: string): string | null => {
      const trimmed = newValue.trim();

      if (trimmed.length < GEOMETRY_NAME_MIN_LENGTH) {
        return t('geometries.inlineEdit.minLength');
      }

      if (trimmed.length > maxLength) {
        return t('geometries.inlineEdit.maxLength');
      }

      return null;
    },
    [maxLength, t]
  );

  // Handle save
  const handleSave = useCallback(() => {
    const trimmed = editValue.trim();
    const validationError = validate(trimmed);

    if (validationError) {
      setError(validationError);
      return;
    }

    // Only save if value changed
    if (trimmed !== value) {
      onSave(trimmed);
    } else {
      onCancel();
    }
  }, [editValue, value, validate, onSave, onCancel]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setError(null);
    setEditValue(value); // Reset to original value
    onCancel();
  }, [onCancel, value]);

  // Handle key down
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation(); // Prevent event from reaching parent listeners (e.g., SlidePanel)
        handleSave();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation(); // Prevent SlidePanel from closing when canceling inline edit
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  // Handle change
  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setEditValue(newValue);

      // Clear error on change
      if (error) {
        setError(null);
      }
    },
    [error]
  );

  if (!isActive) {
    return null;
  }

  // Tooltip text based on device (mobile = no keyboard shortcuts)
  const tooltipKey = isMobile
    ? 'geometries.inlineEdit.pressEnterMobile'
    : 'geometries.inlineEdit.pressEnter';

  return (
    <Group
      wrap="nowrap"
      gap="xs"
      className={`${classes.editorContainer} ${className || ''}`}
    >
      <Tooltip
        label={t(tooltipKey)}
        zIndex={Z_INDEX_TOOLTIP}
        events={{ hover: true, focus: true, touch: false }}
      >
        <TextInput
          ref={inputRef}
          data-inline-edit="true"
          value={editValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || t('geometries.inlineEdit.placeholder')}
          error={error}
          maxLength={maxLength}
          className={classes.input}
          size="xs"
        />
      </Tooltip>

      <div className={classes.buttonsGroup}>
        <Tooltip
          label={t('common.save')}
          zIndex={Z_INDEX_TOOLTIP}
          events={{ hover: true, focus: true, touch: false }}
        >
          <ActionIcon
            variant="subtle"
            color="green"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
            className={classes.confirmButton}
          >
            <IconCheck size={ICON_SIZE_MD} />
          </ActionIcon>
        </Tooltip>

        <Tooltip
          label={t('common.cancel')}
          zIndex={Z_INDEX_TOOLTIP}
          events={{ hover: true, focus: true, touch: false }}
        >
          <ActionIcon
            variant="subtle"
            color="red"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleCancel();
            }}
            className={classes.cancelButton}
          >
            <IconX size={ICON_SIZE_MD} />
          </ActionIcon>
        </Tooltip>
      </div>
    </Group>
  );
});
